import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { toast } from "sonner";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Divider,
  Switch,
  FormControlLabel,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

export default function AdminSettings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    // Geral
    system_name: "Stock Sistema",
    support_email: "suporte@stocksistema.com",
    support_phone: "(11) 99999-9999",
    
    // Financeiro
    monthly_fee: "90.00",
    trial_days: "15",
    grace_days: "5",
    
    // Sistema
    max_users_per_company: "50",
    max_products_per_company: "10000",
    enable_trial: true,
    auto_block_overdue: true,
    
    // Email/SMS
    smtp_host: "",
    smtp_port: "",
    smtp_user: "",
    smtp_password: "",
    
    // Mercado Pago
    mercadopago_public_key: "TEST-b5df1cd6-47c4-45e9-ae51-d90b64bc3981",
    mercadopago_access_token: "TEST-2092089955393254-120716-7e440e2302176f0bd1cb1bcd9aee5d91-389950946",
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({
        ...prev,
        ...response.data
      }));
      toast.success("Configurações carregadas com sucesso");
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings({
      ...settings,
      [field]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/admin/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Configurações salvas com sucesso!");
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error("Erro ao salvar configurações");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Configurações do Sistema
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure parâmetros globais da plataforma
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Configurações Gerais */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Informações Gerais
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nome do Sistema"
                      value={settings.system_name}
                      onChange={(e) => handleChange('system_name', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email de Suporte"
                      type="email"
                      value={settings.support_email}
                      onChange={(e) => handleChange('support_email', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Telefone de Suporte"
                      value={settings.support_phone}
                      onChange={(e) => handleChange('support_phone', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Configurações Financeiras */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Configurações Financeiras
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Mensalidade (R$)"
                      type="number"
                      value={settings.monthly_fee}
                      onChange={(e) => handleChange('monthly_fee', e.target.value)}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Dias de Teste Gratuito"
                      type="number"
                      value={settings.trial_days}
                      onChange={(e) => handleChange('trial_days', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Dias de Carência"
                      type="number"
                      value={settings.grace_days}
                      onChange={(e) => handleChange('grace_days', e.target.value)}
                      helperText="Após vencimento"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Limites do Sistema */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Limites por Empresa
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Máximo de Usuários"
                      type="number"
                      value={settings.max_users_per_company}
                      onChange={(e) => handleChange('max_users_per_company', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Máximo de Produtos"
                      type="number"
                      value={settings.max_products_per_company}
                      onChange={(e) => handleChange('max_products_per_company', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.enable_trial}
                          onChange={(e) => handleChange('enable_trial', e.target.checked)}
                        />
                      }
                      label="Habilitar Período de Teste"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.auto_block_overdue}
                          onChange={(e) => handleChange('auto_block_overdue', e.target.checked)}
                        />
                      }
                      label="Bloquear Automaticamente em Atraso"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Mercado Pago */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Mercado Pago (Pagamentos)
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Public Key"
                      value={settings.mercadopago_public_key}
                      onChange={(e) => handleChange('mercadopago_public_key', e.target.value)}
                      placeholder="TEST-..."
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Access Token"
                      type="password"
                      value={settings.mercadopago_access_token}
                      onChange={(e) => handleChange('mercadopago_access_token', e.target.value)}
                      placeholder="TEST-..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* SMTP (Email) */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  Configurações SMTP (Envio de Emails)
                </Typography>
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Host"
                      value={settings.smtp_host}
                      onChange={(e) => handleChange('smtp_host', e.target.value)}
                      placeholder="smtp.gmail.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Port"
                      type="number"
                      value={settings.smtp_port}
                      onChange={(e) => handleChange('smtp_port', e.target.value)}
                      placeholder="587"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Usuário"
                      value={settings.smtp_user}
                      onChange={(e) => handleChange('smtp_user', e.target.value)}
                      placeholder="seu-email@gmail.com"
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Senha"
                      type="password"
                      value={settings.smtp_password}
                      onChange={(e) => handleChange('smtp_password', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box mt={4} display="flex" gap={2}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={saving}
          >
            {saving ? "Salvando..." : "Salvar Configurações"}
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<RefreshIcon />}
            onClick={loadSettings}
            disabled={saving}
          >
            Restaurar Padrão
          </Button>
        </Box>
      </form>
    </Box>
  );
}
